import { default as moment } from "moment";
import React, { SyntheticEvent } from "react";
import BigCalendar from "react-big-calendar";
import styled, { css } from "styled-components";

import PickLabels from "core/components/label/PickLabels";
import { IViewProps, View } from "core/decorators/View";
import CalendarViewConfig from "./CalendarViewConfig";
import labelProvider from "./labels";


const calendarLabelKeys = labelProvider.getDefaultMap();

type Accessor<V, T extends object> = keyof T | ((o: T) => V);

type stringOrDate = string | Date;

export type RangeData = Date[] | { start: stringOrDate, end: stringOrDate };

export interface ISlotData {
	start: Date;
	end: Date;
	slots: Date[];
	action: "select" | "click" | "doubleClick";
	bounds?: {
		x: number;
		y: number;
		top: number;
		right: number;
		left: number;
		bottom: number;
	};
	box?: {
		clientX: number;
		clientY: number;
		x: number;
		y: number;
	};
}

export interface ICalendarControlProps<T> {
	onViewChange?: (view: Views) => void;

	onNavigate?: (date: Date, view: Views, action: string) => void;

	onRangeChange?: (range: RangeData, view?: Views) => void;

	onSelectEvent?: (event: T, e: SyntheticEvent) => void;

	onSelectSlot?: (slotData: ISlotData) => void;

	eventRenderer?: (event: T) => JSX.Element | string;
	eventAgendaRenderer?: (event: T) => JSX.Element;
}

export interface ICalendarEventAccessors<T extends object> {
	allDayAccessor?: Accessor<boolean, T>;
	endAccessor: Accessor<Date, T>;
	startAccessor: Accessor<Date, T>;
	titleAccessor: Accessor<string, T>;
}

interface ICalendarViewProps<T extends object>
	extends IViewProps<CalendarViewConfig>,
			ICalendarControlProps<T>,
			ICalendarEventAccessors<T> {
	events: T[];
	date: Date;
	view: Views;
}

const localizer = BigCalendar.momentLocalizer(moment);

interface IStyledWrapperProps {
	config: CalendarViewConfig;
}

const StyledWrapper = styled.div<IStyledWrapperProps>(({config}: IStyledWrapperProps) => css`
	& > .rbc-calendar {
		min-height: ${config.minHeight};
	}

	& > .rbc-calendar .rbc-event {
		background-color: ${config.eventBackgroundColor};
		color: ${config.eventColor};
	}

	& > .rbc-calendar .rbc-event.rbc-selected {
		background-color: ${config.eventActiveBackgroundColor};
	}

	& > .rbc-calendar .rbc-today {
		background-color: ${config.activeBackground};
		color: ${config.activeColor};
	}

	& > .rbc-calendar .rbc-date-cell.rbc-current {
		color: ${config.activeColor};
	}

	& > .rbc-calendar .rbc-current-time-indicator {
		background-color: ${config.indicatorColor};
	}

	@media (max-width: 650px) {
		& .rbc-toolbar {
			flex-direction: column;
		}

		& .rbc-btn-group {
			margin: 10px 0;
		}
	}
`);

export enum Views {
	MONTH = "month",
	WEEK = "week",
	DAY = "day",
	AGENDA = "agenda"
}

export const viewOptions = [
	{ value: Views.MONTH, label: "Month" },
	{ value: Views.WEEK, label: "Week" },
	{ value: Views.DAY, label: "Day" },
	{ value: Views.AGENDA, label: "Agenda" }
];

export enum CalendarVariant {
	DEFAULT = "default"
}

@View({id: "calendar", configClass: CalendarViewConfig, variants: CalendarVariant})
export class CalendarView<T extends object> extends React.PureComponent<ICalendarViewProps<T>> {

	public render() {

		const { config, date, events = [] } = this.props;

		return (
			<StyledWrapper config={config}>
				<PickLabels labels={calendarLabelKeys}>
					{
						(messages) => (
							<BigCalendar
								events={events}

								culture={this.props.language}
								messages={messages}
								localizer={localizer}
								allDayAccessor={this.props.allDayAccessor}
								endAccessor={this.props.endAccessor}
								startAccessor={this.props.startAccessor}
								titleAccessor={this.props.titleAccessor}

								onView={this.props.onViewChange}
								onNavigate={this.props.onNavigate}
								onRangeChange={this.props.onRangeChange}
								onSelectEvent={this.props.onSelectEvent}
								onSelectSlot={this.props.onSelectSlot}

								date={date || new Date()}
								view={this.props.view}
								defaultView={config.defaultView || Views.MONTH}
								views={config.views}
							/>
						)
					}
				</PickLabels>
			</StyledWrapper>
		);
	}
}



// WEBPACK FOOTER //
// ./src/common/views/calendar/Calendar.view.tsx